/**
 * HTTP client for the HyggeCare API. Authentication uses only the HttpOnly session cookie (BFF pattern);
 * nothing is kept in localStorage. Every state-changing request carries the antiforgery token in the
 * X-XSRF-TOKEN header; the token is held in memory and refreshed after signing in or out.
 */

export interface ProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  errors?: Record<string, string[]>;
  /** Machine-readable reason of a conflict (e.g. LOCATION_FULL). */
  code?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly title: string;
  readonly detail: string | undefined;
  readonly errors: Record<string, string[]>;
  readonly code: string | undefined;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title ?? 'Błąd');
    this.name = 'ApiError';
    this.status = status;
    this.title = problem.title ?? 'Błąd';
    this.detail = problem.detail;
    this.errors = problem.errors ?? {};
    this.code = problem.code;
  }

  /** First message for a form field (keys are case-insensitive). */
  fieldError(field: string): string | undefined {
    const key = Object.keys(this.errors).find((k) => k.toLowerCase() === field.toLowerCase());
    return key ? this.errors[key]?.[0] : undefined;
  }
}

/** Conflict code: all rooms of the location are taken at that time – staff may book anyway after confirming. */
export const LOCATION_FULL = 'location_full';

/**
 * Sends a staff booking or change; when all rooms of the location are taken, asks whether to save anyway and sends
 * it again with the confirmation.
 */
export async function withLocationFullConfirm<T>(send: (confirmLocationFull: boolean) => Promise<T>): Promise<T> {
  try {
    return await send(false);
  } catch (e) {
    if (!(e instanceof ApiError) || e.code !== LOCATION_FULL) throw e;
    if (window.confirm(e.detail ?? 'W tym czasie wszystkie gabinety są zajęte. Czy na pewno zapisać wizytę?')) return send(true);
    throw new ApiError(409, { title: 'Nie zapisano', detail: 'Nie zapisano – wszystkie gabinety są w tym czasie zajęte.' });
  }
}

let antiforgeryToken: string | null = null;

export async function refreshAntiforgery(): Promise<void> {
  const response = await fetch('/api/v1/auth/antiforgery', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new ApiError(response.status, { title: 'Nie udało się połączyć z serwerem' });
  }
  antiforgeryToken = ((await response.json()) as { token: string }).token;
}

export function clearAntiforgery(): void {
  antiforgeryToken = null;
}

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

async function request<T>(method: Method, path: string, body?: unknown, retried = false): Promise<T> {
  const unsafe = method !== 'GET';
  if (unsafe && !antiforgeryToken) {
    await refreshAntiforgery();
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  } else if (unsafe) {
    headers['Content-Type'] = 'application/json';
    payload = '{}';
  }
  if (unsafe && antiforgeryToken) {
    headers['X-XSRF-TOKEN'] = antiforgeryToken;
  }

  const init: RequestInit = { method, credentials: 'same-origin', headers };
  if (payload !== undefined) {
    init.body = payload;
  }
  const response = await fetch(path, init);

  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as ProblemDetails;
    // An expired antiforgery token (e.g. after the session changed) – refresh once and retry.
    if (unsafe && !retried && response.status === 400 && problem.title === 'Nieprawidłowe żądanie' && !problem.errors) {
      await refreshAntiforgery();
      return request<T>(method, path, body, true);
    }
    throw new ApiError(response.status, problem);
  }

  if (response.status === 204 || response.status === 202 || response.headers.get('Content-Length') === '0') {
    return undefined as T;
  }
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T = void>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T = void>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
};

/** Builds a query string, skipping empty values. */
export function query(params: Record<string, string | number | boolean | null | undefined | string[]>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((v) => search.append(key, v));
    } else {
      search.set(key, String(value));
    }
  }
  const result = search.toString();
  return result ? `?${result}` : '';
}
